import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { useDispatch, useSelector } from 'react-redux';
import appActions from '../../../appActions';
import PrimeTable from '../../../components/PrimeTable';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import { isPermissionCodeValid } from '../../../utils/mics';
import StoreSystemIdFilter from './StoreSystemIdFilter';

const StoreSystemIdList = ({ pageId }) => {

    let navigate = useNavigate();
    const dispatch = useDispatch();
    
    const [deleteRecord, setDeleteRecord] = useState(null); //
    const [deleteTriggered, setDeleteTriggered] = useState(false);

    const readAccess = isPermissionCodeValid(`${pageId}-R`);
    const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    const deleteAccess = isPermissionCodeValid(`${pageId}-D`);

    const storeSysIdList = useSelector((state) => state.storeSysId.storeSysIdList);

    const sysStore = useSelector((state) => state.storeSysId.storeSysId);

    const filters = useSelector((state) => state.storeSysId.filters);

    const getDefaultParams = (filters = {}) => ({
        ...filters,
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder: -1,
    });

    const defaultParams = useMemo(() => getDefaultParams(filters), [filters]);
        
    const defaultParamsWithoutFilters = useMemo(() => getDefaultParams(), []);

    const [formValues, setFormValues] = useState(defaultParams);

    const handleReset = () => {
        dispatch(appActions.STORESYSID_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    };

    const handleRefresh = useCallback(() => {
        setDeleteRecord(null);
        dispatch(appActions.STORESYSID_GET_ALL_REQUEST(defaultParamsWithoutFilters));
    }, [dispatch, defaultParamsWithoutFilters]);

    // Handle delete action
    useEffect(() => {
        if (sysStore?.action === 'delete' && !deleteTriggered) {
            dispatch(appActions.STORESYSID_GET_ALL_REQUEST(defaultParams));
            setDeleteTriggered(true); // Mark as triggered
        }
    }, [sysStore?.action, deleteTriggered, dispatch, defaultParams]);

    // Reset deleteTriggered when action is no longer 'delete'
    useEffect(() => {
        if (sysStore?.action !== 'delete' && deleteTriggered) {
            setDeleteTriggered(false); // Reset flag when not deleting
        }
    }, [sysStore?.action, deleteTriggered]);

    const handleSearch = useCallback((params) => {
        const searchParams = {
            ...params,
            pagesize: params.pagesize || defaultParams?.pagesize,
            pageno: params.pageno || defaultParams?.pageno,
            sort: params.sort || defaultParams?.sort,
            sortOrder: params.sortOrder || defaultParams?.sortOrder,
        };
    
        dispatch(appActions.STORESYSID_GET_ALL_REQUEST(searchParams));
    }, [defaultParams, dispatch]);

    useEffect(() => {
        handleSearch(defaultParams);
    }, []); // Empty dependency array ensures it runs only on mount

    const linkToNew = useCallback(
        () => navigate('/ui/store-system-id-config/detail', { replace: false }),
        [navigate],
    );

    const handleFormValuesChange = (values) => {
        setFormValues(values);
    };


    const handleViewRedirect = useCallback((record) => {
        debugger
        const data = storeSysIdList?.data?.find((t) => t.id === record.id);
        navigate('/ui/store-system-id-config/detail', {
            state: { data, isView: true },
        });
    }, [navigate, storeSysIdList]);

    const handleEditRedirect = useCallback((record) => {
        const data = storeSysIdList?.data?.find((t) => t.id === record.id);
        navigate('/ui/store-system-id-config/detail', { state: { data, isEdit: true } });
    }, [navigate, storeSysIdList]);

    const handleDelete = useCallback((record) => {
        setDeleteRecord(record); 
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        if (deleteRecord) {
            dispatch(appActions.STORESYSID_DELETE_REQUEST({ id: deleteRecord.id }));
            setDeleteRecord(null); // Clear the record after deletion
    
            // Explicitly refresh the table after deletion
            setTimeout(() => {
                handleRefresh();
            }, 500); // Add a slight delay if needed to allow the backend to process
        }
    }, [dispatch, deleteRecord, handleRefresh])

    const renderHeader = () => {
        return (
            <div className="d-flex flex-row gap-10 align-items-center">
                <h1 className="page-header flex"> Stores System ID Configuration </h1>
                <Button icon="pi pi-sync" className='mr-auto tp' data-pr-tooltip="Refresh" rounded text aria-label="Refresh" onClick={handleRefresh} />
                {writeAccess &&
                    <>
                        <Button label="Add New" icon="pi pi-plus" className="p-button-outlined tp" data-pr-tooltip="Add New Store" onClick={linkToNew} />
                    </>
                }
            </div>
        );
    };

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                { readAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                        <i className="pi pi-eye text-blue-500 text-base"></i>
                    </span>
                }
                { writeAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                        <i className="pi pi-pencil text-blue-500 text-base"></i>
                    </span>
                }
                {deleteAccess &&
                    <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                        <i className="pi pi-times-circle text-base text-danger" />
                    </span>
                }
            </div>
        ),
        [handleViewRedirect, handleEditRedirect, handleDelete, readAccess, writeAccess, deleteAccess],
    );

    const onFilterChangeCallback  = useCallback(
        (e) => {

            const { sortField, sortOrder, rows, page } = e;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); // Call search with updated sorting params
        },
        [handleSearch, formValues],
    );

    const columns = [
        { field: 'storeId', header: 'Store ID', sortable: true,},
        { field: 'idType', header: 'ID Type', sortable: true,},
        { field: 'idMin', header: 'Minmun', sortable: true },
        { field: 'idMax', header: 'Maximum', sortable: true, },
         { field: 'idPrefix', header: 'Prefix', sortable: true },
        { field: 'idValue', header: 'Current Value', sortable: true,},
        { field: 'createdOn', header: 'Created On', sortable: true,  width: '140px', columnType : 'datetime'  },
        // { field: 'lastUpdated', header: 'Last Updated', sortable: true, columnType : 'datetime'  },
        // { field: 'lastUpdatedBy', header: 'Last Updated By', sortable: true },
        
    ];

    const renderList = () => {
        return (
            <>
                <div className='pv-15 ph-15'> {renderHeader()} </div>
                
                <div className="bg-white mx-3 pv-15 ph-15 br-6 shadow-white">
                    <StoreSystemIdFilter
                        handleSearch={handleSearch}
                        handleReset={handleReset}
                        onFormValuesChange={handleFormValuesChange} 
                    />
                    <div className='custom-devider'></div>
                    <PrimeTable
                        list={storeSysIdList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        tableName='Stores'
                        isLazy={true}
                        showGenerateXmlBtn={false}
                        pageId={ pageId }
                    />
                </div>
            </>
        );
    };

    return <div className="form_height">
        {renderList()}
        {deleteRecord && (
            <ConfirmationDialog
                message={`Are you sure you want to delete the store config : ${deleteRecord.storeId}?`}
                onAccept={handleDeleteConfirm}
                onReject={() => setDeleteRecord(null)}
            />
        )}
    </div>;
};

export default StoreSystemIdList;
